import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LanguageProvider } from "./context/LanguageContext";
import { CartProvider } from "./context/CartContext";
import { CartDrawer } from "./components/shop/CartDrawer";
import Shop from "./pages/shop/Shop";
import ProductPage from "./pages/shop/ProductPage";
import Checkout, { CartPage } from "./pages/shop/Checkout";
import { OrderStatus, MediaKit, ShopInfo } from "./pages/shop/ShopExtras";
import AdminShop from "./pages/admin/shop/AdminShop";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Leadership from "./pages/Leadership";
import News from "./pages/News";
import NewsDetail from "./pages/NewsDetail";
import Events from "./pages/Events";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Donate from "./pages/Donate";
import Manifesto from "./pages/Manifesto";
import MemberDirectory from "./pages/MemberDirectory";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ManageNews from "./pages/admin/ManageNews";
import ManageMembers from "./pages/admin/ManageMembers";
import ManageEvents from "./pages/admin/ManageEvents";
import ManageLeaders from "./pages/admin/ManageLeaders";
import ManageGallery from "./pages/admin/ManageGallery";
import MediaAnalytics from "./pages/admin/MediaAnalytics";
import LeaderProfile from "./pages/LeaderProfile";
import ThemeSong from "./pages/ThemeSong";
import OurFlag from './pages/OurFlag';
import OurTiger from './pages/OurTiger';
import OurRoundel from './pages/OurRoundel';
import MemberCard from "./pages/MemberCard";
import Join from "./pages/Join";
import VerifyMember from "./pages/VerifyMember";
import Districts from "./pages/Districts";
import DistrictDetail from "./pages/DistrictDetail";
import ManagePartners from "./pages/admin/ManagePartners";
import ManageDistricts from "./pages/admin/ManageDistricts";
import ThemeSongPlayer from "./components/ThemeSongPlayer";

const ToJoin = () => <Navigate to={`/join${useLocation().search}`} replace />;

function App() {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <LanguageProvider>
      <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App">
          <Routes>
            {/* Admin routes without header/footer */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/news" element={<ManageNews />} />
            <Route path="/admin/members" element={<ManageMembers />} />
            <Route path="/admin/events" element={<ManageEvents />} />
            <Route path="/admin/leaders" element={<ManageLeaders />} />
            <Route path="/admin/gallery" element={<ManageGallery />} />
            <Route path="/admin/media-analytics" element={<MediaAnalytics />} />
            <Route path="/admin/partners" element={<ManagePartners />} />
            <Route path="/admin/districts" element={<ManageDistricts />} />
            <Route path="/admin/shop" element={<AdminShop />} />
            
            {/* Public routes with header/footer */}
            <Route
              path="/*"
              element={
                <>
                  <Header />
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/leadership" element={<Leadership />} />
                    <Route path="/leadership/:id" element={<LeaderProfile />} />
                    <Route path="/news" element={<News />} />
                    <Route path="/news/:id" element={<NewsDetail />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/membership" element={<ToJoin />} />
                    <Route path="/donate" element={<Donate />} />
                    <Route path="/manifesto" element={<Manifesto />} />
                    <Route path="/directory" element={<MemberDirectory />} />
                    <Route path="/theme-song" element={<ThemeSong />} />
                    <Route path="/our-flag" element={<OurFlag />} />
                    <Route path="/our-tiger" element={<OurTiger />} />
                    <Route path="/our-roundel" element={<OurRoundel />} />
                    <Route path="/member-card" element={<MemberCard />} />
                    <Route path="/join" element={<Join />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/shop/category/:category" element={<Shop />} />
                    <Route path="/shop/collection/:collection" element={<Shop />} />
                    <Route path="/shop/media-kit" element={<MediaKit />} />
                    {['size-guide', 'shipping-returns', 'faq', 'terms', 'privacy', 'bulk-orders'].map((pg) => <Route key={pg} path={`/shop/${pg}`} element={<ShopInfo page={pg} />} />)}
                    <Route path="/shop/:slug" element={<ProductPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/order" element={<OrderStatus />} />
                    <Route path="/order/:id" element={<OrderStatus />} />
                    <Route path="/districts" element={<Districts />} />
                    <Route path="/districts/:name" element={<DistrictDetail />} />
                    <Route path="/verify/:memberNumber" element={<VerifyMember />} />
                    <Route path="/volunteer" element={<ToJoin />} />
                  </Routes>
                  <Footer />
                </>
              }
            />
          </Routes>
          <Toaster />
          <CartDrawer />
          <ThemeSongPlayer />
        </div>
      </BrowserRouter>
      </CartProvider>
    </LanguageProvider>
  );
}

export default App;
